// SPDX-License-Identifier: AGPL-3.0-only
// Copyright (C) 2026 realvirtual GmbH <https://realvirtual.io>

/**
 * component-bulk-actions.ts — Auto-assign / Unbind-all as component-section
 * actions in the Property Inspector (plan-325 F5).
 *
 * The former SignalBindPopover footer buttons moved here: they register via
 * `componentActionRegistry` against every component type that declares
 * `componentRef + signal` schema fields (plus the synthetic Conveyor slots).
 * Both actions are scoped to the SECTION's component (componentPath + type),
 * not the whole placed element.
 *
 * Confirm gates (no window.confirm — ConnectPanel UI convention):
 *  - Auto-assign: first click computes confident suggestions (exact matches or
 *    token confidence >= 0.75) and arms the button ("Apply N"); the second
 *    click within the confirm window applies them.
 *  - Unbind all: two-click confirm — first click arms ("Confirm unbind all",
 *    3 s window), second click executes.
 */

import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
  componentActionRegistry,
  type ComponentActionContext,
} from '../../core/hmi/rv-component-action-registry';
import { getRegisteredSchemaTypes, getSignalSlotFields } from '../../core/engine/rv-component-registry';
import { baseComponentType } from '../../core/hmi/rv-inspector-helpers';
import { NodeRegistry } from '../../core/engine/rv-node-registry';
import type { RVViewer } from '../../core/rv-viewer';
import type { SignalMapping } from '../layout-planner/rv-layout-store';
import type { SlotRow } from '../../core/hmi/rv-signal-slot-row';
import { buildSlotRowModels, mappingMatchesRow, upsertMappingForRow } from './slot-row-models';
import { collectConnectSignals } from './SignalBindPopover';
import { createSignalBindingPersistence } from './signal-binding-persistence';
import { findSignalBindTarget, signalBindEligibility, signalBindTargetId, type SignalBindTarget } from './signal-bind-target';
import { suggestForElement } from './auto-matcher';
import type { SignalSlotDescriptor } from './slot-descriptors';
import { getConnectSnapshot } from '../../core/hmi/connect-store';

/** Confirm window for both armed actions in milliseconds. */
export const BULK_ACTION_CONFIRM_MS = 3000;

interface ArmedState {
  armedAt: number;
  /** Auto-assign only: the computed confident suggestions. */
  suggestions?: { row: SlotRow; signalName: string }[];
}

/** Armed two-click state per `${nodePath} ${componentType} ${action}`. */
const armed = new Map<string, ArmedState>();

function armKey(ctx: ComponentActionContext, action: string): string {
  return `${ctx.nodePath} ${ctx.componentType} ${action}`;
}

function getArmed(ctx: ComponentActionContext, action: string): ArmedState | null {
  const state = armed.get(armKey(ctx, action));
  if (!state) return null;
  if (Date.now() - state.armedAt > BULK_ACTION_CONFIRM_MS) {
    armed.delete(armKey(ctx, action));
    return null;
  }
  return state;
}

/** Test hook: clear all armed two-click state. */
export function _resetBulkActionStateForTesting(): void {
  armed.clear();
}

interface SectionScope {
  viewer: RVViewer;
  target: SignalBindTarget;
  targetId: string;
  mappings: SignalMapping[];
  rows: SlotRow[];
  persist: (next: SignalMapping[]) => void;
}

/** Resolve the section's bind scope: target + rows filtered to this component. */
function sectionScope(ctx: ComponentActionContext): SectionScope | null {
  const viewer = ctx.viewer;
  const mgr = viewer.signalBindingManager;
  if (!mgr) return null;
  const target = findSignalBindTarget(viewer, ctx.node);
  if (!target) return null;
  if (!signalBindEligibility(viewer, target).eligible) return null;
  const targetId = signalBindTargetId(target);
  const persistence = createSignalBindingPersistence(viewer, target);
  const mappings = persistence.read();
  const base = baseComponentType(ctx.componentType);
  const rootPath = viewer.registry?.getPathForNode(target.node) ?? NodeRegistry.computeNodePath(target.node);
  const relPath = ctx.nodePath === rootPath
    ? '.'
    : (rootPath && ctx.nodePath.startsWith(`${rootPath}/`) ? ctx.nodePath.slice(rootPath.length + 1) : ctx.nodePath);
  const rows = buildSlotRowModels(viewer, mgr, targetId, target.node, mappings)
    .filter((r) => r.kind !== 'unavailable'
      && r.componentPath === relPath
      && (r.componentType === undefined || baseComponentType(r.componentType) === base));
  if (rows.length === 0) return null;
  return {
    viewer,
    target,
    targetId,
    mappings,
    rows,
    persist: (next) => {
      const applied = mgr.applyMappings(targetId, target.node, next);
      persistence.write(applied);
    },
  };
}

/** Confident suggestions for the section's rows (exact, or token >= 0.75). */
function confidentSuggestions(scope: SectionScope): { row: SlotRow; signalName: string }[] {
  const signals = collectConnectSignals(getConnectSnapshot().interfaces);
  const names = signals.filter((s) => !s.conflict).map((s) => s.name);
  if (names.length === 0) return [];
  const out: { row: SlotRow; signalName: string }[] = [];
  for (const row of scope.rows) {
    if (!row.type || !row.direction) continue;
    const descriptor: SignalSlotDescriptor = {
      slot: row.slot, type: row.type, direction: row.direction, aliases: row.aliases ?? [],
    };
    const r = suggestForElement([descriptor], names)[0];
    const s = r?.suggestion;
    if (!s) continue;
    if (s.stage === 'exact' || (s.stage === 'token' && s.confidence >= 0.75)) {
      out.push({ row, signalName: s.signal });
    }
  }
  return out;
}

function applySuggestions(scope: SectionScope, suggestions: { row: SlotRow; signalName: string }[]): void {
  const signals = collectConnectSignals(getConnectSnapshot().interfaces);
  let next: SignalMapping[] = scope.mappings;
  for (const { row, signalName } of suggestions) {
    const source = signals.find((s) => s.name === signalName && !s.conflict);
    if (!source) continue;
    const upserted = upsertMappingForRow(next, row, source);
    if (upserted) next = upserted;
  }
  scope.persist(next);
}

let registered = false;

/** Register the bulk actions for every slot-bearing component type. Idempotent. */
export function registerSignalBulkActions(): void {
  if (registered) return;
  registered = true;

  const types = new Set<string>();
  for (const type of getRegisteredSchemaTypes()) {
    if (getSignalSlotFields(type).length > 0) types.add(type);
  }

  for (const type of types) {
    componentActionRegistry.register(type, [
      {
        id: 'signal-auto-assign',
        label: (ctx) => {
          const state = getArmed(ctx, 'auto');
          return state?.suggestions?.length ? `Apply ${state.suggestions.length}` : 'Auto-assign';
        },
        icon: LinkIcon,
        tooltip: 'Suggest CONNECT signals for the empty slots of this component (confident matches only); a second click applies them',
        order: 200,
        visible: (ctx) => !!ctx.viewer.signalBindingManager && sectionScope(ctx) !== null,
        isActive: (ctx) => getArmed(ctx, 'auto') !== null,
        onClick: (ctx) => {
          const scope = sectionScope(ctx);
          if (!scope) return;
          const state = getArmed(ctx, 'auto');
          if (state?.suggestions?.length) {
            armed.delete(armKey(ctx, 'auto'));
            applySuggestions(scope, state.suggestions);
            return;
          }
          const suggestions = confidentSuggestions(scope);
          if (suggestions.length === 0) return;
          armed.set(armKey(ctx, 'auto'), { armedAt: Date.now(), suggestions });
        },
      },
      {
        id: 'signal-unbind-all',
        label: (ctx) => (getArmed(ctx, 'unbind') ? 'Confirm unbind all' : 'Unbind all'),
        icon: LinkOffIcon,
        tooltip: 'Remove every signal link of this component — first click arms, second click within 3 s confirms',
        order: 201,
        visible: (ctx) => {
          const scope = sectionScope(ctx);
          return !!scope && scope.rows.some((row) => !!row.mapping);
        },
        isActive: (ctx) => getArmed(ctx, 'unbind') !== null,
        onClick: (ctx) => {
          const scope = sectionScope(ctx);
          if (!scope) return;
          if (!getArmed(ctx, 'unbind')) {
            // Two-click confirm (plan-325 F5): arm first, execute second.
            armed.set(armKey(ctx, 'unbind'), { armedAt: Date.now() });
            return;
          }
          armed.delete(armKey(ctx, 'unbind'));
          const next = scope.mappings.filter(
            (m) => !scope.rows.some((row) => mappingMatchesRow(m, row)),
          );
          scope.persist(next);
        },
      },
    ]);
  }
}
